/**
 * World-cluster panels (PXL): Crafting (four-roll), Quests + NPC dialogue, and
 * Direct Trade. Bind to craftIntent/craftResult, questLog/talkIntent/npcDialogue/
 * questAccept/questTurnIn/deliverIntent, vendorList/vendorCatalogue (read-only shelf,
 * AD-403 — no buy verb), and the trade flow. Placeholder art;
 * recipe/ability presentation resolves from the content pack (not yet real).
 */

import { useEffect, useRef, useState } from 'react';

import type { CraftIntentPayload, NpcDialoguePayload } from '@great-cloud/shared/client';

import { describeCraftFailure, describeVendorDenial } from '../../state/store.js';
import { useGame } from '../net-context.js';
import { Bar, Empty, IconGlyph, SectionTitle, Stat } from '../widgets.js';
import { worldFrame } from '../worldFrame.js';
import {
  nameOf,
  npcRolesText,
  objectiveText,
  questStatusLabel,
  stationLabel,
} from '../playerText.js';
import {
  capacityBindConfirmText,
  capacityGatedText,
  capacityUpgradeConfirmText,
  capacityUpgradeRows,
} from './capacity.js';
import { craftableRecipes } from './craftable.js';
import { questCraftCard } from './questCraft.js';
import { useGuestMode } from '../../guest/guestContext.js';
import { NEARBY_NPC_RADIUS_M, nearbyNpcs, othersNearby } from './nearby.js';
import { repairableItems } from './repairable.js';
import { sellableItems } from './sellable.js';
import { shrineLabel, shrineTravelOptions } from './shrineTravel.js';
import { tradeOfferLines, type TradeOfferLine } from './tradeOffer.js';
import { playUiCue, voiceChoice, voiceStop, voiceTalk } from '../../game/audio/index.js';
import { requestClosePanel } from '../Shell.js';
import type { VoiceChoice } from '../../game/audio/voice.js';
import {
  FRESH_CONVERSATION,
  afterChoice,
  dialogueView,
  inProgressHere,
  replyRow,
  type ConversationState,
} from './questDialogue.js';

export function CraftingPanel(): JSX.Element {
  const { state, conn, store } = useGame();
  const [recipeId, setRecipeId] = useState('');
  const [rows, setRows] = useState<{ resource: string; quality: number; quantity: number }[]>([
    { resource: '', quality: 1, quantity: 1 },
  ]);
  const resources = state.inventory?.resources ?? [];
  const craft = state.lastCraft;
  // sprint-assembly-1 (AD-647): what the bag can pay for right now, one click each. The
  // server still judges station, rank and fee — this only fills the form a player would.
  // q-demo-crafting-clarity: the recipe the tracked quest asks for leads, with have / need per
  // input and one Craft button; it is not repeated in the list below.
  const questCard = questCraftCard(state.quests?.quests ?? [], state.recipes, resources);
  const craftable = craftableRecipes(state.recipes, resources).filter(
    (c) => c.recipe.id !== questCard?.recipe.id,
  );
  // A guest never meets the hand-filled recipe-id form: it asks for content ids a player cannot
  // know (the 8-year-old's "neither is the crafting menu itself", Desk #279).
  const guest = useGuestMode();
  // One craft in flight at a time: a double-click must not burn two fees + two input sets.
  const crafting = store.isInFlight('craft', Date.now());
  const sendCraft = (d: CraftIntentPayload): void => {
    if (store.beginRequest('craft', Date.now())) conn.send('craftIntent', d);
  };

  const setRow = (i: number, patch: Partial<(typeof rows)[number]>): void =>
    setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <div className="pxl-list">
      <div className="pxl-faint">
        Stand by the right bench (forge, tanning rack, alchemy table…) and press Craft. Each craft
        costs a small Shard fee.
      </div>
      {questCard ? (
        <>
          <SectionTitle>For your quest</SectionTitle>
          <div className="pxl-row craft-quest-card" data-testid="craft-quest-card">
            <div className="grow">
              <div>{questCard.recipe.name ?? nameOf('recipes', questCard.recipe.id)}</div>
              <div className="pxl-faint">
                {questCard.questName}
                {questCard.recipe.station
                  ? ` · made at the ${stationLabel(questCard.recipe.station)}`
                  : ''}
              </div>
              {questCard.needs.map((n) => (
                <div key={n.resource} className={n.enough ? 'pxl-good' : 'pxl-bad'}>
                  {nameOf('resources', n.resource)} {n.have} / {n.need}
                  {n.enough ? ' ✓' : ` — gather ${n.need - n.have} more`}
                </div>
              ))}
            </div>
            <button
              className="pxl-btn primary"
              disabled={crafting || questCard.inputs === null}
              onClick={() => {
                if (questCard.inputs !== null)
                  sendCraft({ recipeId: questCard.recipe.id, inputs: questCard.inputs });
              }}
            >
              Craft
            </button>
          </div>
        </>
      ) : null}
      {/* With a quest card up, an empty "also" list says nothing useful: it is left out. */}
      {questCard && craftable.length === 0 ? null : (
        <>
          <SectionTitle>{questCard ? 'Also craftable now' : 'Craftable now'}</SectionTitle>
          {craftable.length === 0 ? (
            <Empty>Nothing yet — gather materials first at a resource node.</Empty>
          ) : (
            craftable.map(({ recipe, inputs }) => (
              <div className="pxl-row" key={recipe.id}>
                <div className="grow">
                  <div>{recipe.name ?? recipe.id}</div>
                  <div className="pxl-faint">
                    {recipe.station ? stationLabel(recipe.station) : 'any bench'} ·{' '}
                    {inputs
                      .map((i) => `${nameOf('resources', i.resource)} q${i.quality}×${i.quantity}`)
                      .join(', ')}
                  </div>
                </div>
                <button
                  className="pxl-btn tiny primary"
                  disabled={crafting}
                  onClick={() => sendCraft({ recipeId: recipe.id, inputs })}
                >
                  Craft
                </button>
              </div>
            ))
          )}
        </>
      )}
      {guest ? null : (
        <>
          <SectionTitle>Recipe</SectionTitle>
          <input
            className="pxl-input"
            placeholder="recipe id (content/recipes/<id>)"
            value={recipeId}
            onChange={(e) => setRecipeId(e.target.value)}
          />
          <SectionTitle>Inputs (from your resource stacks)</SectionTitle>
          {resources.length === 0 ? (
            <div className="pxl-faint">No resources yet — gather first at a resource node.</div>
          ) : null}
          {rows.map((r, i) => (
            <div className="pxl-row" key={i}>
              <select
                className="pxl-select grow"
                value={r.resource}
                onChange={(e) => setRow(i, { resource: e.target.value })}
              >
                <option value="">— resource —</option>
                {resources.map((s) => (
                  <option key={`${s.contentId}:${s.quality}`} value={s.contentId}>
                    {nameOf('resources', s.contentId)} (q{s.quality} ×{s.quantity})
                  </option>
                ))}
              </select>
              <input
                className="pxl-input"
                type="number"
                style={{ width: 52 }}
                value={r.quality}
                onChange={(e) => setRow(i, { quality: Number(e.target.value) })}
                title="quality grade"
              />
              <input
                className="pxl-input"
                type="number"
                style={{ width: 52 }}
                value={r.quantity}
                onChange={(e) => setRow(i, { quantity: Number(e.target.value) })}
                title="quantity"
              />
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className="pxl-btn tiny"
              onClick={() => setRows([...rows, { resource: '', quality: 1, quantity: 1 }])}
            >
              + input
            </button>
            <button
              className="pxl-btn primary grow"
              disabled={!recipeId || crafting}
              onClick={() =>
                sendCraft({
                  recipeId,
                  inputs: rows.filter((r) => r.resource),
                })
              }
            >
              Craft
            </button>
          </div>
        </>
      )}
      <SectionTitle>Last result</SectionTitle>
      {craft ? (
        craft.ok && craft.item ? (
          <div className="pxl-row">
            <IconGlyph code={craft.item.contentId} label={craft.item.name} assetRef="TGC-VFX-015" />
            <div className="grow">
              <div className={`r-${craft.item.rarity}`}>{craft.item.name}</div>
              <div className="pxl-faint">
                {craft.item.quality} · {craft.item.rarity} ·{' '}
                {Object.entries(craft.item.rolledStats)
                  .map(([k, v]) => `+${v} ${k}`)
                  .join(', ') || 'no stats'}
              </div>
            </div>
            {craft.balance !== undefined ? (
              <span className="pxl-faint">◈ {craft.balance}</span>
            ) : null}
          </div>
        ) : (
          <div className="pxl-bad">Failed: {describeCraftFailure(craft.reason)}</div>
        )
      ) : (
        <Empty>No craft yet.</Empty>
      )}
      {guest ? null : (
        <div className="pxl-faint" style={{ marginTop: 6 }}>
          Recipe browse/lock indicators resolve from the content pack + mastery/reputation gates
          (descriptors; live enforcement is UIP-6/UIP-7).
        </div>
      )}
    </div>
  );
}

export function QuestsPanel(): JSX.Element {
  const { state, conn, store } = useGame();
  const quests = state.quests?.quests ?? [];
  const dialogue = state.dialogue;
  // In a run the world's NPCs/shrines are another frame: none are nearby (worldFrame.ts).
  const { npcs, shrines } = worldFrame(state);
  const vendor = state.vendor;
  const repairable = repairableItems(state.inventory?.items ?? []);
  const sellable = sellableItems(
    state.inventory?.items ?? [],
    vendor?.ok ? vendor.entries.map((e) => e.itemId) : [],
  );
  // Own position from the latest snapshot; null until the first one lands.
  const self = state.selfEntityId !== null ? state.entities.get(state.selfEntityId) : undefined;
  const selfAt = self?.x !== undefined && self.z !== undefined ? { x: self.x, z: self.z } : null;
  // q-demo-dialogue-playtest-2026-10-29: the NPC already in conversation is not offered again.
  const nearby = othersNearby(nearbyNpcs(npcs, selfAt), dialogue?.npcId ?? null);
  // AD-672 S4: at a shrine, the zone's other shrines are a paid trip away.
  const travel = shrineTravelOptions(shrines, selfAt);
  // AD-775 §6.1: a vendor sells carrying space; a raise asks once, then burns.
  const capacityRows = capacityUpgradeRows(state.inventory?.capacity);
  const [confirmCap, setConfirmCap] = useState<string | null>(null);

  // Spoken lines (AD-759): speak a conversation when it opens; cut it when the panel closes or the
  // next talk begins. The subtitles are the dialogue text rendered below. The talk number keeps a
  // re-opened panel from replaying an old conversation.
  const talk = state.dialogueTalk;
  const dialogueRef = useRef(dialogue);
  dialogueRef.current = dialogue;
  // Goodbye closes the window; the farewell it speaks must outlive it (it is the answer to the
  // goodbye), so the close does not cut a line the goodbye started.
  const farewellSaid = useRef(false);
  useEffect(() => {
    farewellSaid.current = false;
    const d = dialogueRef.current;
    if (d) voiceTalk(talk, d);
    return () => {
      if (!farewellSaid.current) voiceStop();
    };
  }, [talk]);
  // AD-870: opening speaks only the greeting; every other line answers a choice made here (ask
  // about a quest, accept / decline, ask how it goes, turn in, a topic, goodbye). `convo` is what
  // those choices revealed in THIS conversation; a new talk starts fresh.
  const [convo, setConvo] = useState<ConversationState>(FRESH_CONVERSATION);
  useEffect(() => setConvo(FRESH_CONVERSATION), [talk]);
  const choose = (d: NpcDialoguePayload, choice: VoiceChoice): void => {
    setConvo(afterChoice(d, choice));
    voiceChoice(d, choice);
  };
  /**
   * Goodbye ends the conversation and closes the window the talk opened (owner playtest
   * 2026-10-29: "Goodbye doesn't close the NPC interaction popup"). Every NPC has one, with or
   * without a farewell line; the farewell is spoken, and its subtitle shows the way a bark's does,
   * since the window that would have shown it is gone.
   */
  const sayGoodbye = (d: NpcDialoguePayload): void => {
    if (d.farewell) {
      farewellSaid.current = true;
      voiceChoice(d, { kind: 'farewell' });
      store.toast('info', `${d.name}: “${d.farewell}”`, 'bark');
    }
    store.endConversation();
    requestClosePanel('quests');
  };
  const view = dialogue ? dialogueView(dialogue) : null;
  const inProgress = dialogue ? inProgressHere(dialogue, state.quests) : [];
  // Where the NPC's reply is drawn: under the row of the quest it answers (replyRow).
  const reply = (
    <div className="pxl-topic-line" data-testid="npc-reply">
      {convo.said}
    </div>
  );
  const replyUnder = replyRow(convo, [
    ...(dialogue?.offers ?? []).map((o) => o.questId),
    ...inProgress.map((q) => q.questId),
    ...(dialogue?.turnIns ?? []),
  ]);

  // The dialogue and the shelf come FIRST (sprint-assembly-1, AD-647): "talk" must show its
  // answer where the player is looking, not below a zone's worth of NPC rows.
  return (
    <div className="pxl-list">
      {dialogue ? (
        <>
          <SectionTitle>{dialogue.name}</SectionTitle>
          {dialogue.greeting ? <div>{dialogue.greeting}</div> : null}
          {convo.said && replyUnder === null ? reply : null}
          {(view?.freeLines ?? []).map((l, i) => (
            <div key={i} className="pxl-faint">
              {l}
            </div>
          ))}
          {/* AD-870: a quest is asked about first; its pitch is the answer, then the player
              accepts or declines, and each answer has its own line. */}
          {dialogue.offers.map((o) => (
            <div className="pxl-topic" key={o.questId} data-offer={o.questId}>
              <div className="pxl-row">
                <div className="grow">{o.name}</div>
                {convo.askedQuest === o.questId ? (
                  <>
                    <button
                      className="pxl-btn tiny primary"
                      onClick={() => {
                        conn.send('questAccept', { questId: o.questId });
                        playUiCue('quest.accept'); // AD-861
                        choose(dialogue, { kind: 'accept', questId: o.questId });
                      }}
                    >
                      accept
                    </button>
                    <button
                      className="pxl-btn tiny"
                      onClick={() => choose(dialogue, { kind: 'decline', questId: o.questId })}
                    >
                      decline
                    </button>
                  </>
                ) : (
                  <button
                    className="pxl-btn tiny"
                    onClick={() => choose(dialogue, { kind: 'offer', questId: o.questId })}
                  >
                    ask about it
                  </button>
                )}
              </div>
              {convo.askedQuest === o.questId && o.offerText ? (
                <div className="pxl-topic-line">{o.offerText}</div>
              ) : null}
              {replyUnder === o.questId ? reply : null}
            </div>
          ))}
          {inProgress.map((q) => (
            <div className="pxl-topic" key={q.questId} data-progress={q.questId}>
              <div className="pxl-row">
                <div className="grow">{q.name}</div>
                <button
                  className="pxl-btn tiny"
                  onClick={() => choose(dialogue, { kind: 'progress', questId: q.questId })}
                >
                  how goes it?
                </button>
              </div>
              {replyUnder === q.questId ? reply : null}
            </div>
          ))}
          {/* The turn-in's answer (the `complete` line and the rewards) is the completion card,
              raised by the server's confirmation (questTurnedIn), not by this tap. */}
          {dialogue.turnIns.map((q) => (
            <div className="pxl-topic" key={q} data-turn-in={q}>
              <div className="pxl-row">
                <div className="grow">{quests.find((l) => l.questId === q)?.name ?? q}</div>
                <button
                  className="pxl-btn tiny primary"
                  onClick={() => conn.send('questTurnIn', { questId: q })}
                >
                  turn in
                </button>
              </div>
              {replyUnder === q ? reply : null}
            </div>
          ))}
          {/* q-npc-lore-talk-option (Owner Desk #279): the world's background sits behind a
              choice, like a quest offer — take the quest, or ask to hear more. */}
          {(view?.topics ?? []).map((t) => (
            <div key={t.id} className="pxl-topic" data-topic={t.id}>
              <div className="pxl-row">
                <div className="grow">{t.ask}</div>
                <button
                  className={`pxl-btn tiny${convo.askedTopic === t.id ? ' primary' : ''}`}
                  aria-pressed={convo.askedTopic === t.id}
                  onClick={() => choose(dialogue, { kind: 'topic', topicId: t.id })}
                >
                  ask
                </button>
              </div>
              {convo.askedTopic === t.id
                ? t.lines.map((l, i) => (
                    <div key={i} className="pxl-topic-line">
                      {l}
                    </div>
                  ))
                : null}
            </div>
          ))}
          <div className="pxl-row">
            <div className="grow" />
            <button className="pxl-btn tiny" onClick={() => sayGoodbye(dialogue)}>
              goodbye
            </button>
          </div>
        </>
      ) : null}

      {vendor ? (
        <>
          <SectionTitle>Shelf — {nameOf('npcs', vendor.npcId)}</SectionTitle>
          {vendor.ok ? (
            <>
              <div className="pxl-faint">{npcRolesText(vendor.roles)}</div>
              {vendor.entries.length === 0 ? <Empty>Nothing stocked.</Empty> : null}
              {vendor.entries.map((e) => (
                <div className="pxl-row" key={e.itemId}>
                  <div className="grow">{e.name}</div>
                  <span className="pxl-faint">◈ {e.priceShards}</span>
                </div>
              ))}
              {vendor.unpriced > 0 ? (
                <div className="pxl-faint">
                  {vendor.unpriced} unpriced line{vendor.unpriced === 1 ? '' : 's'} not shown.
                </div>
              ) : null}
              <div className="pxl-faint" style={{ marginTop: 6 }}>
                Display only — buying is not wired yet.
              </div>
              {vendor.roles.includes('vendor') ? (
                <>
                  <SectionTitle>Repair</SectionTitle>
                  {repairable.length === 0 ? <Empty>Nothing needs mending.</Empty> : null}
                  {repairable.map((r) => (
                    <div className="pxl-row" key={r.id}>
                      <div className="grow">
                        <div>{r.name}</div>
                        <div className="pxl-faint">
                          {r.durabilityCurrent}/{r.durabilityMax}
                          {r.worn ? ' · worn' : ''}
                        </div>
                      </div>
                      <button
                        className="pxl-btn tiny"
                        onClick={() =>
                          conn.send('repair', { npcId: vendor.npcId, itemInstanceId: r.id })
                        }
                      >
                        repair
                      </button>
                    </div>
                  ))}
                  <SectionTitle>Sell</SectionTitle>
                  {sellable.length === 0 ? <Empty>Nothing in your bag to sell.</Empty> : null}
                  {sellable.map((s) => (
                    <div className="pxl-row" key={s.id}>
                      <div className="grow">{s.name}</div>
                      <button
                        className="pxl-btn tiny"
                        onClick={() =>
                          conn.send('vendorSell', { npcId: vendor.npcId, itemInstanceId: s.id })
                        }
                      >
                        sell
                      </button>
                    </div>
                  ))}
                  <div className="pxl-faint">
                    Vendors pay half an item&apos;s worth, up to a daily limit.
                  </div>
                  {capacityRows.length > 0 ? (
                    <>
                      <SectionTitle>Carrying space</SectionTitle>
                      {capacityRows.map((r) => (
                        <div className="pxl-row" key={r.key}>
                          <div className="grow">
                            <div>{r.label}</div>
                            <div className="pxl-faint">
                              {confirmCap === r.key
                                ? capacityUpgradeConfirmText(r)
                                : confirmCap === `bind:${r.key}`
                                  ? capacityBindConfirmText(r)
                                  : r.next && r.containerRequired && !r.container
                                    ? capacityGatedText(r)
                                    : r.next
                                      ? `${r.size.toLocaleString()} → ${r.next.cap.toLocaleString()} ${r.unit} · ◈ ${r.next.cost.toLocaleString()}`
                                      : `${r.size.toLocaleString()} ${r.unit} · as large as it gets`}
                            </div>
                          </div>
                          {r.next && (confirmCap === r.key || confirmCap === `bind:${r.key}`) ? (
                            <>
                              <button
                                className="pxl-btn tiny primary"
                                onClick={() => {
                                  conn.send('capacityUpgrade', {
                                    npcId: vendor.npcId,
                                    cap: r.key,
                                    tier: r.tier,
                                    // §6.2: a binding names the container it consumes.
                                    ...(confirmCap === `bind:${r.key}` && r.container
                                      ? { containerId: r.container.itemId }
                                      : {}),
                                  });
                                  setConfirmCap(null);
                                }}
                              >
                                confirm
                              </button>
                              <button className="pxl-btn tiny" onClick={() => setConfirmCap(null)}>
                                cancel
                              </button>
                            </>
                          ) : r.next ? (
                            <>
                              {/* §6.2b: a gated raise offers the bind alone. */}
                              {r.containerRequired ? null : (
                                <button
                                  className="pxl-btn tiny"
                                  onClick={() => setConfirmCap(r.key)}
                                >
                                  raise
                                </button>
                              )}
                              {r.container ? (
                                <button
                                  className="pxl-btn tiny"
                                  onClick={() => setConfirmCap(`bind:${r.key}`)}
                                >
                                  bind
                                </button>
                              ) : null}
                            </>
                          ) : null}
                        </div>
                      ))}
                      <div className="pxl-faint">
                        Raising a cap spends its Shards for good — nothing else grows it.
                      </div>
                    </>
                  ) : null}
                </>
              ) : null}
            </>
          ) : (
            <div className="pxl-bad">{describeVendorDenial(vendor)}</div>
          )}
        </>
      ) : null}

      {travel ? (
        <>
          <SectionTitle>Shrine — {shrineLabel(travel.here)}</SectionTitle>
          {travel.to.length === 0 ? <Empty>No other shrine in this zone.</Empty> : null}
          {travel.to.map(({ shrine, distanceM }) => (
            <div className="pxl-row" key={shrine.id}>
              <div className="grow">
                <div>{shrineLabel(shrine)}</div>
                <div className="pxl-faint">{Math.round(distanceM)} m</div>
              </div>
              <button
                className="pxl-btn tiny"
                onClick={() => conn.send('travel', { shrineId: shrine.id })}
              >
                travel
              </button>
            </div>
          ))}
          {travel.to.length > 0 ? (
            <div className="pxl-faint">
              Shrines carry you for a Shard fee — never out of a fight.
            </div>
          ) : null}
        </>
      ) : null}

      {/* In a conversation with no one else near, there is no one else to list. */}
      {dialogue && nearby.near.length === 0 ? null : <SectionTitle>Nearby NPCs</SectionTitle>}
      {npcs.length === 0 ? <Empty>No NPCs in this zone.</Empty> : null}
      {!dialogue && npcs.length > 0 && nearby.near.length === 0 ? (
        <Empty>No one within {NEARBY_NPC_RADIUS_M} m — follow a gold ! to a questgiver.</Empty>
      ) : null}
      {nearby.near.map(({ npc: n, distanceM }) => (
        <div className="pxl-row" key={n.id}>
          <IconGlyph code={n.id} label={n.name} assetRef="TGC-UI-032" size={24} />
          <div className="grow">
            <div>{n.name}</div>
            <div className="pxl-faint">
              {[
                npcRolesText(n.roles),
                Number.isFinite(distanceM) ? `${Math.round(distanceM)} m` : '',
              ]
                .filter((part) => part !== '')
                .join(' · ')}
            </div>
          </div>
          <button className="pxl-btn tiny" onClick={() => conn.send('talkIntent', { npcId: n.id })}>
            talk
          </button>
          {n.roles.some((r) => r === 'vendor' || r === 'trainer') ? (
            <button
              className="pxl-btn tiny"
              onClick={() => conn.send('vendorList', { npcId: n.id })}
            >
              shelf
            </button>
          ) : null}
        </div>
      ))}
      {nearby.further > 0 ? (
        <div className="pxl-faint">
          {nearby.further} more in this zone, further than {NEARBY_NPC_RADIUS_M} m.
        </div>
      ) : null}

      <SectionTitle>Quest log ({quests.length})</SectionTitle>
      {quests.length === 0 ? <Empty>No active quests — talk to an NPC.</Empty> : null}
      {quests.map((q) => (
        <div key={q.questId} className="pxl-row" style={{ display: 'block' }}>
          <div className="pxl-stat">
            <span className="k">{q.name}</span>
            <span className="v pxl-badge">{questStatusLabel(q.status)}</span>
          </div>
          {q.objectives.map((o, i) => (
            <div key={i} style={{ marginTop: 3 }}>
              <div className="pxl-faint">
                {objectiveText(o)} — {o.progress}/{o.count} {o.done ? '✓' : ''}
              </div>
              <Bar value={o.progress} max={o.count} height={7} color="var(--pxl-good)" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function OfferLines({ lines }: { lines: TradeOfferLine[] }): JSX.Element {
  return (
    <ul className="pxl-faint" style={{ margin: '2px 0 0 14px', padding: 0 }}>
      {lines.map((l, i) => (
        <li key={i} className={l.rarity ? `r-${l.rarity}` : undefined}>
          {l.text}
        </li>
      ))}
    </ul>
  );
}

export function TradePanel(): JSX.Element {
  const { state, conn } = useGame();
  const trade = state.trade;
  const req = state.tradeRequest;
  const [itemIds, setItemIds] = useState('');
  const [shards, setShards] = useState(0);
  const players = [...state.entities.values()].filter(
    // AD-833: a summoned image rides as kind `player`; it is nobody to trade with.
    (e) => e.kind === 'player' && e.id !== state.selfEntityId && e.summonOf === undefined,
  );
  const ownItems = state.inventory?.items ?? [];

  return (
    <div className="pxl-list">
      {req ? (
        <div className="pxl-row">
          <div className="grow">Trade request from {req.fromName}</div>
          <button
            className="pxl-btn tiny primary"
            onClick={() => conn.send('tradeRespond', { accept: true })}
          >
            accept
          </button>
          <button
            className="pxl-btn tiny"
            onClick={() => conn.send('tradeRespond', { accept: false })}
          >
            decline
          </button>
        </div>
      ) : null}

      {trade ? (
        <>
          <SectionTitle>
            Trade — {trade.phase === 'confirm' ? 'Phase 2: confirm' : 'Phase 1: compose'} (rev{' '}
            {trade.revision})
          </SectionTitle>
          {/* Every leg of both offers, not a count: what you confirm is what you get. */}
          <div className="pxl-row" style={{ display: 'block' }} data-testid="trade-offers">
            <div>
              You {trade.youConfirmed ? '✓ confirmed' : trade.youSubmitted ? 'locked' : 'editing'}{' '}
              give:
            </div>
            <OfferLines lines={tradeOfferLines(trade.yourOffer, ownItems, trade.items)} />
            <div style={{ marginTop: 4 }}>
              Partner{' '}
              {trade.partnerConfirmed
                ? '✓ confirmed'
                : trade.partnerSubmitted
                  ? 'locked'
                  : 'editing'}{' '}
              gives:
            </div>
            <OfferLines lines={tradeOfferLines(trade.partnerOffer, ownItems, trade.items)} />
          </div>

          {trade.phase === 'compose' ? (
            trade.youSubmitted ? (
              // Phase 1, locked: waiting for the partner to submit. Un-submit to edit.
              <>
                <Empty>Your offer is locked. Waiting for your partner to submit…</Empty>
                <button className="pxl-btn grow" onClick={() => conn.send('tradeUnsubmit', {})}>
                  unlock / edit offer
                </button>
              </>
            ) : (
              // Phase 1, composing: edit the offer freely, then submit to lock it.
              <>
                <SectionTitle>Your offer</SectionTitle>
                <input
                  className="pxl-input"
                  placeholder="item instance ids (comma-separated)"
                  value={itemIds}
                  onChange={(e) => setItemIds(e.target.value)}
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    className="pxl-input"
                    type="number"
                    style={{ width: 80 }}
                    value={shards}
                    onChange={(e) => setShards(Number(e.target.value))}
                    title="shards to offer"
                  />
                  <button
                    className="pxl-btn grow"
                    onClick={() =>
                      conn.send('tradeOffer', {
                        itemIds: itemIds
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean),
                        resources: [],
                        shards,
                      })
                    }
                  >
                    update offer
                  </button>
                </div>
                <button
                  className="pxl-btn primary grow"
                  onClick={() => conn.send('tradeSubmit', { revision: trade.revision })}
                >
                  submit and lock offer
                </button>
              </>
            )
          ) : (
            // Phase 2: both offers locked. Confirm or back out. Any change resets both.
            <>
              <SectionTitle>Review and confirm</SectionTitle>
              <Empty>
                Both offers are locked. Changing either offer resets both confirmations.
              </Empty>
              {trade.youConfirmed ? (
                <Empty>You confirmed. Waiting for your partner…</Empty>
              ) : (
                <button
                  className="pxl-btn primary grow"
                  onClick={() => conn.send('tradeConfirm', { revision: trade.revision })}
                >
                  confirm trade
                </button>
              )}
              <button className="pxl-btn grow" onClick={() => conn.send('tradeUnsubmit', {})}>
                back out (edit)
              </button>
            </>
          )}

          <button className="pxl-btn grow" onClick={() => conn.send('tradeCancel', {})}>
            cancel trade
          </button>
        </>
      ) : (
        <>
          <SectionTitle>Nearby players</SectionTitle>
          {players.length === 0 ? <Empty>No other players in range.</Empty> : null}
          {players.map((p) => (
            <div className="pxl-row" key={p.id}>
              <div className="grow">{p.name ?? `player ${p.id}`}</div>
              <button
                className="pxl-btn tiny"
                onClick={() => conn.send('tradeRequest', { targetEntityId: p.id })}
              >
                trade
              </button>
            </div>
          ))}
          <Stat k="Trade window" v="open on request / accept" />
        </>
      )}
    </div>
  );
}
